/**
 * OnboardingTour — Nice, gentle guided tour for non-technical users
 *
 * Runs on first visit to simple mode. Explains the app in plain language
 * with visuals and "Try it" buttons.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  MobileStepper,
  Card,
  CardContent,
} from '@mui/material';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import PlayArrow from '@mui/icons-material/PlayArrow';
import { useAppDispatch } from '../store/hooks';
import { setNonTechnicalMode } from '../store/slices/uiSlice';

interface OnboardingTourProps {
  open: boolean;
  onClose: () => void;
}

const TOUR_STEPS = [
  {
    title: 'Welcome to your simple map tool',
    content:
      'GeoVera helps you work with maps without needing to be a computer expert. Think of it like a digital notebook for your land or projects.',
  },
  {
    title: 'Everything starts with big friendly buttons',
    content:
      "On the home screen you'll see cards like 'Draw my land' or 'Measure an area'. Just click one and we'll walk you through it step by step.",
  },
  {
    title: 'You can always switch to advanced tools',
    content:
      "If you ever need the full power (for surveyors and engineers), there's an 'Advanced Mode' button at the top. You can switch back anytime.",
  },
  {
    title: 'Your work is safe and private',
    content:
      'Everything saves automatically. You can come back later and pick up exactly where you left off.',
  },
];

const OnboardingTour: React.FC<OnboardingTourProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleFinish = () => {
    onClose();
    // Optionally start the user in simple mode if not already
    dispatch(setNonTechnicalMode(true));
  };

  const current = TOUR_STEPS[activeStep];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {current.title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {current.content}
          </Typography>
        </Box>

        <Card variant="outlined" sx={{ mt: 2, bgcolor: 'action.hover' }}>
          <CardContent>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              Tip: Start with the big cards on the home screen. They're designed to feel like using
              a phone app.
            </Typography>
          </CardContent>
        </Card>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Button onClick={handleBack} disabled={activeStep === 0} startIcon={<KeyboardArrowLeft />}>
          Back
        </Button>

        <MobileStepper
          variant="dots"
          steps={TOUR_STEPS.length}
          position="static"
          activeStep={activeStep}
          sx={{ flex: 1, mx: 2, background: 'transparent' }}
          nextButton={<span />}
          backButton={<span />}
        />

        {activeStep === TOUR_STEPS.length - 1 ? (
          <Button variant="contained" onClick={handleFinish} endIcon={<PlayArrow />}>
            Let's Get Started
          </Button>
        ) : (
          <Button variant="contained" onClick={handleNext} endIcon={<KeyboardArrowRight />}>
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OnboardingTour;
